<template>
  <div class="dk-leads-page">

    <!-- ── Page header (só na lista) ── -->
    <div v-if="viewMode === 'list'" class="dk-leads-header">
      <div class="dk-leads-header__left">
        <div class="dk-leads-header__icon-wrap">
          <feather-icon icon="UsersIcon" size="20" />
        </div>
        <div>
          <h2 class="dk-leads-header__title">Seus Leads</h2>
          <p class="dk-leads-header__sub">Gerencie e filtre os contatos capturados pelos seus fluxos</p>
        </div>
        <span v-if="items.length" class="dk-leads-header__count">{{ items.length }} lead{{ items.length !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- ── View toggle + Kanban config ── -->
    <div class="dk-leads-topbar">
      <div class="dk-leads-view-toggle">
        <button
          class="dk-view-btn"
          :class="{ 'dk-view-btn--active': viewMode === 'list' }"
          @click="viewMode = 'list'"
        >
          <feather-icon icon="ListIcon" size="15" class="mr-50" />
          Lista
        </button>
        <button
          class="dk-view-btn"
          :class="{ 'dk-view-btn--active': viewMode === 'kanban' }"
          @click="viewMode = 'kanban'"
        >
          <feather-icon icon="ColumnsIcon" size="15" class="mr-50" />
          Pipeline
        </button>
      </div>
      <div v-if="viewMode === 'kanban'" class="dk-leads-topbar__right">
        <div class="dk-pipeline-filter">
          <feather-icon icon="MessageSquareIcon" size="14" class="dk-pipeline-filter__icon" />
          <select v-model="pipelineChatFilter" class="dk-pipeline-filter__select">
            <option value="">Todos os chats</option>
            <option v-for="c in chats.filter(function(c) { return c.status == 1 || c.status === '1' || c.status === true })" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <button
          class="dk-cols-btn"
          :class="{ 'dk-cols-btn--selecting': kanbanSelectMode }"
          @click="toggleKanbanSelectMode"
        >
          <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
          {{ kanbanSelectMode ? 'Cancelar seleção' : 'Selecionar' }}
        </button>
        <button class="dk-cols-btn" @click="showColConfig = !showColConfig">
          <feather-icon icon="SlidersIcon" size="14" class="mr-50" />
          Colunas
          <feather-icon :icon="showColConfig ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="13" class="ml-25" />
        </button>
      </div>
    </div>
    <!-- Column config panel -->
    <div v-if="viewMode === 'kanban' && showColConfig" class="dk-col-config">
      <div
        v-for="s in allPipelineStages"
        :key="s.key"
        class="dk-col-config__item"
      >
        <input type="checkbox" :checked="s.enabled" @change="togglePipelineStage(s.key)" class="dk-col-config__check" />
        <span class="dk-col-config__dot" :style="{ background: s.color }"></span>
        <input
          type="text"
          :value="s.label"
          class="dk-col-config__name"
          @blur="renameStage(s.key, $event.target.value)"
          @keydown.enter="$event.target.blur()"
        />
      </div>
      <button class="dk-col-config__save" @click="savePipelineConfig">
        <feather-icon icon="CheckIcon" size="13" class="mr-50" />
        Salvar
      </button>
    </div>

    <!-- ── Selection action bar (lista e kanban) — aparece logo abaixo do topbar ── -->
    <transition name="dk-bar-slide">
      <div v-if="selectedLeads.length > 0" class="dk-leads-selection-bar">
        <span class="dk-leads-selection-bar__info">
          <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
          {{ selectedLeads.length }} lead{{ selectedLeads.length !== 1 ? 's' : '' }} selecionado{{ selectedLeads.length !== 1 ? 's' : '' }}
        </span>
        <div class="dk-leads-selection-bar__actions">
          <button
            class="dk-selection-btn dk-selection-btn--iaqfaz"
            :disabled="sendingToIaqfaz"
            @click="sendToIaqfaz"
          >
            <feather-icon icon="SendIcon" size="13" class="mr-50" />
            {{ sendingToIaqfaz ? 'Enviando...' : 'Enviar para iAqFaz' }}
          </button>
          <button class="dk-selection-btn dk-selection-btn--clear" @click="selectedLeads = []">
            <feather-icon icon="XIcon" size="13" />
          </button>
        </div>
      </div>
    </transition>

    <!-- ── Kanban View ── -->
    <LeadsKanban
      v-if="viewMode === 'kanban' && getUserData && getUserData.id"
      :user-id="getUserData.id"
      :enabled-stages="enabledStages"
      :stage-config="allPipelineStages"
      :lead-capture-config-by-chat="leadCaptureConfigByChat"
      :items="pipelineChatFilter ? items.filter(function(l) { return l.id_chat === pipelineChatFilter }) : items"
      :select-mode="kanbanSelectMode"
      :selected-leads="selectedLeads"
      :sent-leads="iaqfazSentLeads"
      @toggle-select="toggleLeadSelection"
    />

    <!-- ── Filter toolbar ── -->
    <div v-show="viewMode === 'list'" class="dk-leads-toolbar">
      <!-- Search -->
      <div class="dk-leads-search">
        <span class="dk-leads-search__icon">
          <feather-icon icon="SearchIcon" size="15" />
        </span>
        <b-form-input
          v-model="dadosFiltra"
          placeholder="Pesquisar dados do lead..."
          class="dk-leads-search__input"
          @input="filtrarPorDados()"
        />
      </div>

      <!-- Flow select -->
      <div class="dk-toolbar-select-wrap">
        <feather-icon icon="GitMergeIcon" size="14" class="dk-toolbar-select-wrap__icon" />
        <el-select
          v-model="selectedValue1"
          placeholder="Todos os fluxos"
          class="dk-el-select"
        >
          <el-option value="Todos" label="Todos os fluxos" />
          <el-option
            v-for="item in chats"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>

      <!-- Period select -->
      <div class="dk-toolbar-select-wrap">
        <feather-icon icon="CalendarIcon" size="14" class="dk-toolbar-select-wrap__icon" />
        <el-select
          v-model="selectedValue"
          placeholder="Período"
          class="dk-el-select"
        >
          <el-option
            v-for="item in option"
            :key="item.title"
            :label="item.title"
            :value="item.title"
          />
        </el-select>
      </div>

      <!-- Per page -->
      <div class="dk-per-page">
        <feather-icon icon="ListIcon" size="14" class="dk-per-page__icon" />
        <b-form-select
          v-model="perPage"
          :options="pageOptions"
          class="dk-per-page__select"
        />
      </div>
    </div>

    <!-- ── Table ── -->
    <div v-show="viewMode === 'list'" class="dk-leads-table-wrap" style="position: relative;">
      <b-table
        :per-page="isTrialExpired ? 999 : perPage"
        :current-page="isTrialExpired ? 1 : currentPage"
        :items="items"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        :filter="filter"
        :filter-included-fields="filterOn"
        responsive
        hover
        class="dk-leads-table"
        :class="{ 'dk-leads-table--trial-expired': isTrialExpired }"
        thead-class="dk-leads-table__head"
        :tbody-tr-class="trialRowClass"
        @filtered="onFiltered"
      >
        <!-- Checkbox de seleção: cabeçalho -->
        <template #head(select)>
          <input
            type="checkbox"
            class="dk-row-check"
            :checked="selectableItems.length > 0 && selectedLeads.length === selectableItems.length"
            :indeterminate.prop="selectedLeads.length > 0 && selectedLeads.length < selectableItems.length"
            @change="selectedLeads.length === selectableItems.length ? selectedLeads = [] : selectedLeads = selectableItems.map(i => i.id_conversa)"
          />
        </template>

        <!-- Checkbox de seleção: célula -->
        <template #cell(select)="row">
          <span v-if="iaqfazSentLeads.includes(row.item.id_conversa)" class="dk-row-sent-icon" title="Já enviado ao iAqFaz">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="#7c3aed" stroke-width="2"/>
              <polyline points="8 12 11 15 16 9" stroke="#7c3aed" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <input
            v-else
            type="checkbox"
            class="dk-row-check"
            :checked="selectedLeads.includes(row.item.id_conversa)"
            @change="toggleLeadSelection(row.item.id_conversa)"
            @click.stop
          />
        </template>

        <!-- Nome clicável → vai direto para o CRM -->
        <template #cell(nome)="row">
          <button class="dk-lead-name-btn" @click="mudarRota(row.item.id_conversa)">
            <div
              class="dk-lead-avatar-ring"
              :style="getLeadRingStyle(row.item)"
              :title="getLeadProgressTooltip(row.item)"
            >
              <div class="dk-lead-avatar">{{ getInitials(row.item.nome) }}</div>
            </div>
            <div class="dk-lead-name-info">
              <span class="dk-lead-name-text">{{ row.item.nome || '—' }}</span>
              <span class="dk-lead-progress-hint">{{ getLeadProgressHint(row.item) }}</span>
              <span v-if="iaqfazSentLeads.includes(row.item.id_conversa)" class="dk-iaqfaz-sent-badge">
                <svg width="9" height="9" fill="none" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                iAqFaz
              </span>
              <span v-else class="dk-lead-name-hint">ver CRM</span>
            </div>
          </button>
        </template>

        <template #cell(name)="data">
          <div class="dk-leads-fluxo">
            <feather-icon icon="GitMergeIcon" size="12" class="dk-leads-fluxo__icon" />
            {{ data.value }}
          </div>
        </template>

        <!-- Badge de canal / fonte (Google, Meta, Orgânico) -->
        <template #cell(source)="data">
          <span class="dk-source-badge" :class="'dk-source-badge--' + (data.value || 'organic')">
            <span v-if="data.value === 'google'" class="dk-source-badge__svg" v-html="googleSvg" />
            <span v-else-if="data.value === 'meta'" class="dk-source-badge__svg" v-html="metaSvg" />
            <feather-icon v-else icon="GlobeIcon" size="11" />
            {{ sourceLabel(data.value) }}
          </span>
        </template>

        <template #cell(created_at)="data">
          <span class="dk-leads-date">
            <feather-icon icon="ClockIcon" size="12" class="mr-25" />
            {{ data.value }}
          </span>
        </template>

        <template #cell(delete)="row">
          <div class="dk-leads-actions">
            <button
              class="dk-action-btn dk-action-btn--view"
              title="Abrir CRM do lead"
              @click="mudarRota(row.item.id_conversa)"
            >
              <feather-icon icon="ExternalLinkIcon" size="15" />
            </button>
            <button
              class="dk-action-btn dk-action-btn--delete"
              title="Remover lead"
              @click="showModalDelete(row.item.id_conversa)"
            >
              <feather-icon icon="TrashIcon" size="15" />
            </button>
          </div>
        </template>
      </b-table>

      <!-- Trial expired overlay on blurred leads -->
      <div v-if="isTrialExpired && items.length > 5" class="dk-trial-leads-overlay">
        <div class="dk-trial-leads-overlay__card">
          <div class="dk-trial-leads-overlay__icon">
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <h4 class="dk-trial-leads-overlay__title">Leads bloqueados</h4>
          <p class="dk-trial-leads-overlay__text">
            Seu período de teste expirou. Faça upgrade para ver todos os seus leads.
          </p>
          <a :href="kiwifyCheckoutUrl" target="_blank" class="dk-trial-leads-overlay__btn">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <polyline points="17 8 12 3 7 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            Fazer Upgrade
          </a>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="items.length === 0" class="dk-leads-empty">
        <feather-icon icon="UsersIcon" size="36" class="dk-leads-empty__icon" />
        <p class="dk-leads-empty__text">Nenhum lead encontrado</p>
        <p class="dk-leads-empty__sub">Ajuste os filtros ou aguarde novos contatos</p>
      </div>
    </div>

    <!-- ── Modal detalhes do lead ── -->
    <b-modal
      id="modal-lead-detail"
      :title="leadSelecionadoDetalhe ? leadSelecionadoDetalhe.nome || 'Detalhes do Lead' : 'Detalhes do Lead'"
      hide-footer
      size="md"
      centered
    >
      <div v-if="leadSelecionadoDetalhe" class="dk-lead-detail">

        <!-- Avatar + nome -->
        <div class="dk-lead-detail__hero">
          <div class="dk-lead-detail__avatar">
            {{ getInitials(leadSelecionadoDetalhe.nome) }}
          </div>
          <div>
            <div class="dk-lead-detail__name">{{ leadSelecionadoDetalhe.nome || '—' }}</div>
            <div class="dk-lead-detail__flow">
              <feather-icon icon="GitMergeIcon" size="12" class="mr-25" />
              {{ leadSelecionadoDetalhe.name }}
            </div>
          </div>
          <span class="dk-badge dk-badge--success ml-auto">
            <feather-icon icon="CheckCircleIcon" size="11" class="mr-25" />
            Completo
          </span>
        </div>

        <!-- Dados de contato -->
        <div class="dk-lead-detail__section">
          <p class="dk-lead-detail__section-title">Contato</p>
          <div class="dk-lead-detail__grid">
            <div v-if="leadSelecionadoDetalhe.whatsapp" class="dk-lead-detail__field">
              <feather-icon icon="PhoneIcon" size="14" class="dk-lead-detail__field-icon" />
              <div>
                <span class="dk-lead-detail__label">WhatsApp</span>
                <span class="dk-lead-detail__value">{{ leadSelecionadoDetalhe.whatsapp }}</span>
              </div>
            </div>
            <div v-if="leadSelecionadoDetalhe['e-mail'] || leadSelecionadoDetalhe.email" class="dk-lead-detail__field">
              <feather-icon icon="MailIcon" size="14" class="dk-lead-detail__field-icon" />
              <div>
                <span class="dk-lead-detail__label">E-mail</span>
                <span class="dk-lead-detail__value">{{ leadSelecionadoDetalhe['e-mail'] || leadSelecionadoDetalhe.email }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Mensagem / pergunta / problema -->
        <div v-if="getLeadMessage(leadSelecionadoDetalhe)" class="dk-lead-detail__section">
          <p class="dk-lead-detail__section-title">Mensagem</p>
          <div class="dk-lead-detail__message">
            {{ getLeadMessage(leadSelecionadoDetalhe) }}
          </div>
        </div>

        <!-- Metadados -->
        <div class="dk-lead-detail__section">
          <p class="dk-lead-detail__section-title">Informações</p>
          <div class="dk-lead-detail__grid">
            <div class="dk-lead-detail__field">
              <feather-icon icon="ClockIcon" size="14" class="dk-lead-detail__field-icon" />
              <div>
                <span class="dk-lead-detail__label">Capturado em</span>
                <span class="dk-lead-detail__value">{{ leadSelecionadoDetalhe.created_at }}</span>
              </div>
            </div>
            <div v-if="leadSelecionadoDetalhe.origem" class="dk-lead-detail__field">
              <feather-icon icon="GlobeIcon" size="14" class="dk-lead-detail__field-icon" />
              <div>
                <span class="dk-lead-detail__label">Origem</span>
                <span class="dk-lead-detail__value">{{ leadSelecionadoDetalhe.origem | truncateUrl }}</span>
              </div>
            </div>
            <div v-if="leadSelecionadoDetalhe.page" class="dk-lead-detail__field">
              <feather-icon icon="LinkIcon" size="14" class="dk-lead-detail__field-icon" />
              <div>
                <span class="dk-lead-detail__label">Página</span>
                <span class="dk-lead-detail__value">{{ leadSelecionadoDetalhe.page | truncateUrl }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Rodapé com ações -->
        <div class="dk-lead-detail__footer">
          <button
            class="dk-action-btn dk-action-btn--view dk-action-btn--lg"
            @click="mudarRota(leadSelecionadoDetalhe.id_conversa); setdadosFiltro(); $bvModal.hide('modal-lead-detail')"
          >
            <feather-icon icon="MessageSquareIcon" size="15" class="mr-50" />
            Ver conversa completa
          </button>
          <button
            class="dk-action-btn dk-action-btn--delete dk-action-btn--lg"
            @click="showModalDelete(leadSelecionadoDetalhe.id_conversa); $bvModal.hide('modal-lead-detail')"
          >
            <feather-icon icon="TrashIcon" size="15" class="mr-50" />
            Remover
          </button>
        </div>
      </div>
    </b-modal>

    <!-- ── Pagination ── -->
    <div v-if="!isTrialExpired && viewMode === 'list'" class="dk-leads-pagination">
      <span class="dk-leads-pagination__info">
        Página {{ currentPage }} · {{ items.length }} resultados
      </span>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="right"
        size="sm"
        class="dk-pagination mb-0"
      />
    </div>

    <!-- ── Modal resultado iAqFaz ── -->
    <b-modal
      id="modal-iaqfaz-results"
      hide-header
      hide-footer
      centered
      size="sm"
      body-class="p-0"
    >
      <div v-if="iaqfazSendResults" class="dk-iaqfaz-modal">
        <div class="dk-iaqfaz-modal__header" :class="iaqfazSendResults.failed === 0 ? 'dk-iaqfaz-modal__header--success' : 'dk-iaqfaz-modal__header--partial'">
          <div class="dk-iaqfaz-modal__icon-wrap">
            <feather-icon :icon="iaqfazSendResults.failed === 0 ? 'CheckCircleIcon' : 'AlertCircleIcon'" size="22" />
          </div>
          <button class="dk-delete-modal__close" @click="$bvModal.hide('modal-iaqfaz-results')">
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>
        <div class="dk-iaqfaz-modal__body">
          <h4 class="dk-iaqfaz-modal__title">Envio para iAqFaz</h4>
          <div class="dk-iaqfaz-modal__stats">
            <div class="dk-iaqfaz-stat dk-iaqfaz-stat--sent">
              <span class="dk-iaqfaz-stat__num">{{ iaqfazSendResults.sent }}</span>
              <span class="dk-iaqfaz-stat__label">enviado{{ iaqfazSendResults.sent !== 1 ? 's' : '' }}</span>
            </div>
            <div v-if="iaqfazSendResults.failed > 0" class="dk-iaqfaz-stat dk-iaqfaz-stat--failed">
              <span class="dk-iaqfaz-stat__num">{{ iaqfazSendResults.failed }}</span>
              <span class="dk-iaqfaz-stat__label">falha{{ iaqfazSendResults.failed !== 1 ? 's' : '' }}</span>
            </div>
          </div>
          <div v-if="iaqfazSendResults.failed > 0" class="dk-iaqfaz-errors">
            <div
              v-for="r in iaqfazSendResults.results.filter(x => x.status === 'failed')"
              :key="r.id_conversa"
              class="dk-iaqfaz-error-item"
            >
              <feather-icon icon="AlertTriangleIcon" size="12" class="mr-50" />
              <span>{{ r.nome || r.id_conversa.slice(0, 8) + '...' }}</span>
              <span class="dk-iaqfaz-error-msg">{{ r.error }}</span>
            </div>
          </div>
        </div>
        <div class="dk-iaqfaz-modal__footer">
          <button class="dk-delete-modal__btn dk-delete-modal__btn--cancel" @click="$bvModal.hide('modal-iaqfaz-results')">
            Fechar
          </button>
        </div>
      </div>
    </b-modal>

    <!-- ── Modal delete ── -->
    <b-modal
      id="modal-delete-lead"
      hide-header
      hide-footer
      centered
      size="sm"
      body-class="p-0"
    >
      <div class="dk-delete-modal">
        <!-- Cabeçalho vermelho -->
        <div class="dk-delete-modal__header">
          <div class="dk-delete-modal__icon-wrap">
            <feather-icon icon="Trash2Icon" size="22" />
          </div>
          <button class="dk-delete-modal__close" @click="$bvModal.hide('modal-delete-lead')">
            <feather-icon icon="XIcon" size="16" />
          </button>
        </div>

        <!-- Corpo -->
        <div class="dk-delete-modal__body">
          <h4 class="dk-delete-modal__title">Excluir lead</h4>
          <p class="dk-delete-modal__text">
            Tem certeza que deseja excluir este lead?<br>
            <strong>Essa ação não pode ser desfeita.</strong>
          </p>
        </div>

        <!-- Rodapé com ações -->
        <div class="dk-delete-modal__footer">
          <button
            class="dk-delete-modal__btn dk-delete-modal__btn--cancel"
            @click="$bvModal.hide('modal-delete-lead')"
          >
            Cancelar
          </button>
          <button
            class="dk-delete-modal__btn dk-delete-modal__btn--confirm"
            @click="deletarChat(); $bvModal.hide('modal-delete-lead')"
          >
            <feather-icon icon="TrashIcon" size="14" class="mr-25" />
            Sim, excluir
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BTable, BRow, BCol, BFormGroup, BAvatar, BPagination, BBadge, BButton,
  BFormSelect, BCard, BFormInput, BCardText,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import { useApi } from '@/composables/useApi'
import trialMixin from '@/mixins/trialMixin'
import LeadsKanban from './LeadsKanban.vue'

const apiBack = useApi()

export default {
  mixins: [trialMixin],
  components: {
    BFormSelect,
    BButton,
    BCard,
    BBadge,
    BPagination,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BTable,
    BFormInput,
    BCardText,
    vSelect,
    LeadsKanban,
  },
  filters: {
    truncateUrl(url) {
      if (!url) return ''
      try {
        const u = new URL(url)
        return u.hostname.replace('www.', '')
      } catch {
        return url.length > 30 ? url.substring(0, 30) + '…' : url
      }
    },
  },
  data() {
    const routeQuery = (this.$route && this.$route.query) || {}
    return {
      viewMode: localStorage.getItem('leads_view') || 'kanban',
      showColConfig: false,
      pipelineChatFilter: routeQuery.pipelineChat || localStorage.getItem('filtroPipelineChat') || '',
      allPipelineStages: [
        { key: 'novo', label: 'Novo', color: '#3B82F6', enabled: true },
        { key: 'contato', label: 'Contato', color: '#8B5CF6', enabled: true },
        { key: 'qualificado', label: 'Qualificado', color: '#F59E0B', enabled: true },
        { key: 'proposta', label: 'Proposta', color: '#E86A10', enabled: true },
        { key: 'ganho', label: 'Ganho', color: '#10B981', enabled: true },
        { key: 'perdido', label: 'Perdido', color: '#EF4444', enabled: true },
      ],
      enabledStages: ['novo', 'contato', 'qualificado', 'proposta', 'ganho', 'perdido'],
      fields: [
        { key: 'select', label: '' },
        { key: 'nome', label: 'Lead' },
        { key: 'name', label: 'Fluxo' },
        { key: 'source', label: 'Canal' },
        { key: 'created_at', label: 'Capturado em', sortable: true },
        { key: 'delete', label: 'Ações' },
      ],
      sortBy: '',
      resposta: '',
      dadosFiltra: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      currentPage: 1,
      totalRows: 1,
      perPage: 5,
      pageOptions: [3, 5, 10, 15],
      selectedLeads: [],
      sendingToIaqfaz: false,
      iaqfazSendResults: null,
      iaqfazWebhookUrl: '',
      iaqfazSentLeads: (function () {
        try {
          const u = JSON.parse(localStorage.getItem('userData') || '{}')
          const raw = localStorage.getItem('iaqfaz_sent_leads_' + (u.id || 'x'))
          return raw ? JSON.parse(raw) : []
        } catch { return [] }
      })(),
      leadCaptureConfigByChat: {},
      kanbanSelectMode: false,
      leadSelecionado: '',
      leadSelecionadoDetalhe: null,
      selectedValue1: '',
      selectedValue: '',
      option: [
        { title: 'Ontem' },
        { title: 'Hoje' },
        { title: 'Ultimos 7 dias' },
        { title: 'Ultimos 30 dias' },
        { title: 'Tudo' },
      ],
      items: [],
      chats: [],
      googleSvg: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>',
      metaSvg: '<svg width="12" height="12" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
    }
  },
  computed: {
    getUserData() {
      return JSON.parse(localStorage.getItem('userData'))
    },
    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    selectableItems() {
      return this.items.filter(i => !this.iaqfazSentLeads.includes(i.id_conversa))
    },
  },
  watch: {
    viewMode(val) {
      localStorage.setItem('leads_view', val)
      this.showColConfig = false
      this.selectedLeads = []
      this.kanbanSelectMode = false
    },
    selectedValue1(newValue) {
      if (newValue === 'Todos' && this.selectedValue === 'Tudo' && this.dadosFiltra === '') {
        this.listaLead()
        return
      }
      if (newValue === 'Todos' && this.selectedValue === '' && this.dadosFiltra === '') {
        this.listaLead()
        return
      }
      if (newValue) {
        if (this.dadosFiltra !== '') {
          this.filtrarPorDados()
        } else {
          this.filtroFluxo()
        }
      }
    },
    selectedValue(newValue) {
      if (this.dadosFiltra !== '') {
        this.filtrarPorDados()
      } else {
        this.filtroData()
      }
      if (newValue === 'Tudo' && this.selectedValue1 === 'Todos') {
        this.listaLead()
      }
    },
    dadosFiltra() {
      this.filtrarPorDados()
    },
    pipelineChatFilter(newValue) {
      localStorage.setItem('filtroPipelineChat', newValue || '')
    },
  },
  async mounted() {
    await this.loadPipelineConfig()
    await this.listaLead()
    await this.listaChat()
    await this.loadLeadCaptureConfigs()
    await this.getdadosFiltro()
    this.totalRows = this.items.length

    const leadsPerPage = localStorage.getItem('leadsPerPage')
    this.perPage = leadsPerPage ? parseInt(leadsPerPage) : 5

    document.addEventListener('updatePerPage', event => {
      this.perPage = event.detail
    })
  },
  methods: {
    togglePipelineStage(key) {
      const stage = this.allPipelineStages.find(s => s.key === key)
      if (!stage) return
      const enabledCount = this.allPipelineStages.filter(s => s.enabled).length
      if (stage.enabled && enabledCount <= 2) return
      stage.enabled = !stage.enabled
      this.enabledStages = this.allPipelineStages.filter(s => s.enabled).map(s => s.key)
    },
    renameStage(key, newLabel) {
      const stage = this.allPipelineStages.find(s => s.key === key)
      if (stage && newLabel.trim()) stage.label = newLabel.trim()
    },
    async savePipelineConfig() {
      try {
        await apiBack.put(`pipeline-config/${this.getUserData.id}`, {
          stages: this.allPipelineStages.map((s, i) => ({
            key: s.key, label: s.label, color: s.color, order: i, enabled: s.enabled,
          })),
        })
        this.enabledStages = this.allPipelineStages.filter(s => s.enabled).map(s => s.key)
        this.showColConfig = false
        this.$message({ type: 'success', message: 'Colunas salvas!' })
      } catch { this.$message({ type: 'error', message: 'Erro ao salvar' }) }
    },
    async loadPipelineConfig() {
      try {
        const { data } = await apiBack.get(`pipeline-config/${this.getUserData.id}`)
        if (Array.isArray(data) && data.length) {
          this.allPipelineStages = data.map(s => ({
            key: s.key, label: s.label, color: s.color, enabled: s.enabled !== false,
          }))
          this.enabledStages = this.allPipelineStages.filter(s => s.enabled).map(s => s.key)
        }
      } catch {}
    },
    async setdadosFiltro() {
      localStorage.setItem('filtroDADOS', this.dadosFiltra)
      localStorage.setItem('filtroCHAT', this.selectedValue1)
      localStorage.setItem('filtroPeriodo', this.selectedValue)
      localStorage.setItem('filtroPipelineChat', this.pipelineChatFilter || '')
      localStorage.setItem('leadsPerPage', this.perPage.toString())
    },
    async getdadosFiltro() {
      const query = (this.$route && this.$route.query) || {}
      this.selectedValue1 = query.flow || localStorage.getItem('filtroCHAT') || ''
      this.selectedValue = query.period || localStorage.getItem('filtroPeriodo') || ''
      this.dadosFiltra = query.search || localStorage.getItem('filtroDADOS') || ''
      this.pipelineChatFilter = query.pipelineChat || localStorage.getItem('filtroPipelineChat') || ''
      localStorage.setItem('filtroDADOS', '')
      localStorage.setItem('filtroCHAT', '')
      localStorage.setItem('filtroPeriodo', '')
    },
    async listaLead() {
      await apiBack.get(`leads/${this.getUserData.id}`)
        .then(response => {
          this.items = response.data
        })
        .catch(error => console.log(error))
    },
    async deletarChat() {
      await apiBack.delete(`/leads/${this.leadSelecionado}`)
        .then(() => {
          this.listaLead()
          this.$message({ type: 'success', message: 'Deletado com sucesso' })
        })
        .catch(err => console.error(err))
    },
    async listaChat() {
      await apiBack.get(`chat/${this.getUserData.id}`)
        .then(response => { this.chats = response.data })
        .catch(error => console.log(error))
    },
    normalizeFieldLabel(label) {
      return String(label || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
    },
    async loadLeadCaptureConfigs() {
      const chatsAtivos = (this.chats || []).filter(c => c && c.id)
      const entries = await Promise.all(chatsAtivos.map(async chat => {
        try {
          const { data } = await apiBack.get(`chat-ai/${chat.id}`)
          let fields = []
          try {
            fields = data && data.ai_capture_fields ? JSON.parse(data.ai_capture_fields) : []
          } catch {
            fields = []
          }
          const required = fields
            .filter(f => f && f.required && f.label)
            .map(f => String(f.label).trim())
          return [chat.id, required]
        } catch {
          return [chat.id, []]
        }
      }))

      this.leadCaptureConfigByChat = entries.reduce((acc, [chatId, required]) => {
        acc[chatId] = required
        return acc
      }, {})
    },
    getLeadRequiredFields(lead) {
      const byChat = this.leadCaptureConfigByChat[lead.id_chat] || []
      if (byChat.length) return byChat
      return ['Nome', 'Email', 'WhatsApp']
    },
    isLeadFieldAnswered(lead, requiredFieldLabel) {
      const target = this.normalizeFieldLabel(requiredFieldLabel)
      const campos = Array.isArray(lead.campos) ? lead.campos : []
      const aliases = {
        nome: ['nome', 'name'],
        email: ['email', 'e-mail', 'mail'],
        whatsapp: ['whatsapp', 'telefone', 'celular', 'phone'],
      }
      // Fallback robusto: usa campos normalizados já resolvidos do lead
      // quando labels de "campos" vierem divergentes.
      if (aliases.nome.includes(target) && String(lead.nome || '').trim()) return true
      if (aliases.email.includes(target) && String((lead.email || lead['e-mail'] || '')).trim()) return true
      if (aliases.whatsapp.includes(target) && String(lead.whatsapp || '').trim()) return true

      const accepted = aliases[target] || [target]
      const campo = campos.find(c => accepted.includes(this.normalizeFieldLabel(c && c.label)))
      if (!campo) return false
      return String(campo.value || '').trim().length > 0
    },
    getLeadProgressStats(lead) {
      const required = this.getLeadRequiredFields(lead)
      const total = Math.max(required.length, 1)
      const completed = required.reduce((acc, label) => acc + (this.isLeadFieldAnswered(lead, label) ? 1 : 0), 0)
      return { total, completed, pct: Math.round((completed / total) * 100) }
    },
    getLeadRingStyle(lead) {
      const stats = this.getLeadProgressStats(lead)
      const step = 360 / stats.total
      const gap = Math.min(6, step * 0.3)
      const fillColor = '#22c55e'
      const emptyColor = 'rgba(148, 163, 184, 0.42)'
      const sepColor = 'rgba(15, 23, 42, 0.5)'
      const segments = []
      for (let i = 0; i < stats.total; i++) {
        const start = i * step
        const segEnd = (i + 1) * step
        const fillEnd = Math.max(start, segEnd - gap)
        const color = i < stats.completed ? fillColor : emptyColor
        segments.push(`${color} ${start}deg ${fillEnd}deg`)
        segments.push(`${sepColor} ${fillEnd}deg ${segEnd}deg`)
      }
      return { background: `conic-gradient(${segments.join(',')})` }
    },
    getLeadProgressHint(lead) {
      const stats = this.getLeadProgressStats(lead)
      return `${stats.completed}/${stats.total} obrigatórios`
    },
    getLeadProgressTooltip(lead) {
      const stats = this.getLeadProgressStats(lead)
      return `Coleta do lead: ${stats.completed}/${stats.total} campos obrigatórios (${stats.pct}%)`
    },
    async filtroFluxo() {
      if (this.dadosFiltra !== '') {
        await apiBack.post('filterDados', {
          periodo: this.selectedValue === '' ? 'Tudo' : this.selectedValue,
          id_chat: (this.selectedValue1 === '' || this.selectedValue1 === 'Todos') ? 0 : this.selectedValue1,
          value: this.dadosFiltra,
          id_user: this.getUserData.id,
        }).then(response => { this.items = response.data }).catch(console.log)
      } else if (this.selectedValue === '') {
        await apiBack.get(`leadsChat/${this.selectedValue1}`)
          .then(response => { this.items = response.data }).catch(console.log)
      } else {
        await apiBack.post('getDataChat', {
          periodo: this.selectedValue,
          id_chat: this.selectedValue1,
        }).then(response => { this.items = response.data }).catch(console.log)
      }
    },
    openLeadModal(lead) {
      this.leadSelecionadoDetalhe = lead
      this.$bvModal.show('modal-lead-detail')
    },
    getInitials(nome) {
      if (!nome) return '?'
      return nome.trim().split(' ').slice(0, 2).map(n => n[0].toUpperCase()).join('')
    },
    getLeadMessage(lead) {
      return lead.pergunta || lead.problema || lead.mensagem || lead.message || ''
    },
    showModalDelete(item) {
      this.leadSelecionado = item
      this.$bvModal.show('modal-delete-lead')
    },
    async filtroData() {
      if (this.selectedValue1 === '' || this.selectedValue1 === 'Todos') {
        await apiBack.post('getData', {
          periodo: this.selectedValue,
          id_user: this.getUserData.id,
        }).then(response => { this.items = response.data }).catch(console.log)
      } else {
        await apiBack.post('getDataChat', {
          periodo: this.selectedValue,
          id_chat: this.selectedValue1,
        }).then(response => { this.items = response.data }).catch(console.log)
      }
    },
    mudarRota(id) {
      this.setdadosFiltro()
      return this.$router.push({
        name: 'Detalheleads',
        params: { id_lead: id },
        query: {
          flow: this.selectedValue1 || '',
          period: this.selectedValue || '',
          search: this.dadosFiltra || '',
          pipelineChat: this.pipelineChatFilter || '',
          view: this.viewMode || 'kanban',
        },
      })
    },
    sourceLabel(source) {
      const labels = { google: 'Google', meta: 'Meta', utm: 'Campanha', organic: 'Orgânico' }
      return labels[source] || 'Orgânico'
    },
    trialRowClass(item, type) {
      if (!this.isTrialExpired || type !== 'row') return ''
      const idx = this.items.indexOf(item)
      return idx >= 5 ? 'dk-trial-blurred-row' : ''
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    toggleLeadSelection(idConversa) {
      if (this.iaqfazSentLeads.includes(idConversa)) return
      const idx = this.selectedLeads.indexOf(idConversa)
      if (idx === -1) {
        this.selectedLeads.push(idConversa)
      } else {
        this.selectedLeads.splice(idx, 1)
      }
    },
    toggleKanbanSelectMode() {
      this.kanbanSelectMode = !this.kanbanSelectMode
      if (!this.kanbanSelectMode) this.selectedLeads = []
    },
    async sendToIaqfaz() {
      if (!this.selectedLeads.length || this.sendingToIaqfaz) return

      const IAQFAZ_URL = 'https://prggcddwvgcvjhcgendu.supabase.co/functions/v1/form-webhook?company_id=cd8dac92-ac38-456a-9d26-71f1e115dc35&skip_workflow=true&skip_email=true'

      if (!this.iaqfazWebhookUrl) {
        // 1. tenta integração salva no localStorage
        const cached = localStorage.getItem('iaqfaz_webhook_url')
        if (cached) {
          this.iaqfazWebhookUrl = cached
        } else {
          // 2. tenta buscar da API
          try {
            const { data } = await apiBack.get('Integracoes?id_user=' + this.getUserData.id)
            const iaqfaz = data.find(function (i) { return i.modelo === 'iAqFaz' && i.url })
            if (iaqfaz) {
              this.iaqfazWebhookUrl = iaqfaz.url
              localStorage.setItem('iaqfaz_webhook_url', iaqfaz.url)
            }
          } catch {}
        }
        // 3. fallback: URL padrão do iAqFaz
        if (!this.iaqfazWebhookUrl) {
          this.iaqfazWebhookUrl = IAQFAZ_URL
          localStorage.setItem('iaqfaz_webhook_url', IAQFAZ_URL)
        }
      }

      this.sendingToIaqfaz = true
      try {
        const response = await apiBack.post('send-to-iaqfaz', {
          leads: this.selectedLeads,
          id_user: this.getUserData.id,
          webhook_url: this.iaqfazWebhookUrl,
        })
        this.iaqfazSendResults = response.data
        // Persiste os leads enviados com sucesso para bloquear re-envio
        const sentIds = (response.data.results || [])
          .filter(r => r.status === 'sent')
          .map(r => r.id_conversa)
        if (sentIds.length) {
          this.iaqfazSentLeads = [...new Set([...this.iaqfazSentLeads, ...sentIds])]
          try {
            localStorage.setItem('iaqfaz_sent_leads_' + this.getUserData.id, JSON.stringify(this.iaqfazSentLeads))
          } catch {}
        }
        this.$bvModal.show('modal-iaqfaz-results')
        if (response.data.sent > 0) {
          this.selectedLeads = []
          this.kanbanSelectMode = false
        }
      } catch (err) {
        const msg = (err && err.response && err.response.data && err.response.data.error) || 'Erro ao enviar para iAqFaz'
        this.$message({ type: 'error', message: msg })
      } finally {
        this.sendingToIaqfaz = false
      }
    },
    async filtrarPorDados() {
      await apiBack.post('filterDados', {
        periodo: this.selectedValue === '' ? 'Tudo' : this.selectedValue,
        id_chat: (this.selectedValue1 === '' || this.selectedValue1 === 'Todos') ? 0 : this.selectedValue1,
        value: this.dadosFiltra,
        id_user: this.getUserData.id,
      }).then(response => { this.items = response.data }).catch(console.log)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

$dk-navy:   #0D3349;
$dk-orange: #E86A10;
$dk-gold:   #F5B935;
$dk-red:    #C12B2B;

// ── Page root ───────────────────────────────────────
.dk-leads-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0 24px;
}

// ── Header ──────────────────────────────────────────
.dk-leads-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: var(--dk-bg-card, #fff);
  border-radius: 14px;
  border: 1px solid var(--dk-border, #edf1f4);
  box-shadow: var(--dk-shadow-sm, 0 1px 4px rgba($dk-navy, 0.05));
}
.dk-leads-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.dk-leads-header__icon-wrap {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba($dk-orange, 0.12), rgba($dk-gold, 0.08));
  border: 1px solid rgba($dk-orange, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $dk-orange;
  flex-shrink: 0;
}
.dk-leads-header__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--dk-text-primary, $dk-navy);
  margin: 0 0 2px;
  line-height: 1.2;
}
.dk-leads-header__sub {
  font-size: 0.78rem;
  color: var(--dk-text-muted, #9ca5b0);
  margin: 0;
}
.dk-leads-header__count {
  background: rgba($dk-orange, 0.1);
  color: $dk-orange;
  font-size: 0.74rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 3px 10px;
  border: 1px solid rgba($dk-orange, 0.2);
  white-space: nowrap;
}

// ── Top bar ──────────────────────────────────────────
.dk-leads-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}
.dk-leads-topbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dk-pipeline-filter {
  display: flex;
  align-items: center;
  position: relative;
}
.dk-pipeline-filter__icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  pointer-events: none;
}
.dk-pipeline-filter__select {
  padding: 8px 14px 8px 34px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  appearance: auto;
  min-width: 160px;
  option { background: #1e2a3a; color: #e2e8f0; }
  &:focus { border-color: $dk-orange; outline: none; }
}

.dk-cols-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.06);
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  font-family: inherit;
  &:hover { border-color: $dk-orange; color: $dk-orange; background: rgba(232,106,16,0.08); }
}
.dk-col-config {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
}
.dk-col-config__item {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.12);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #cbd5e1;
  user-select: none;
  &:hover { border-color: rgba(255,255,255,0.25); background: rgba(255,255,255,0.07); }
}
.dk-col-config__check {
  width: 16px;
  height: 16px;
  accent-color: $dk-orange;
  cursor: pointer;
}
.dk-col-config__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dk-col-config__name {
  border: 1px solid transparent;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #cbd5e1;
  padding: 4px 8px;
  border-radius: 6px;
  width: 120px;
  font-family: inherit;
  &:focus { border-color: $dk-orange; background: rgba(255,255,255,0.07); outline: none; }
}
.dk-col-config__save {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: $dk-orange;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  margin-left: auto;
  &:hover { opacity: 0.9; }
}

// ── View Toggle ──────────────────────────────────────
.dk-leads-view-toggle {
  display: flex;
  gap: 4px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 4px;
  width: fit-content;
}
.dk-view-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  font-family: inherit;

  &:hover { color: #e2e8f0; }
  &--active {
    background: rgba(232,106,16,0.15);
    color: #E86A10;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }
}

// ── Toolbar ─────────────────────────────────────────
.dk-leads-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px 16px;
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 12px;
  box-shadow: var(--dk-shadow-sm, 0 1px 4px rgba($dk-navy, 0.04));
}

// Search field
.dk-leads-search {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 200px;
  background: var(--dk-bg-input, #f7f9fb);
  border: 1px solid var(--dk-border-input, #e4eaee);
  border-radius: 9px;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: $dk-orange;
    box-shadow: 0 0 0 3px rgba($dk-orange, 0.1);
    background: var(--dk-bg-card, #fff);
  }

  &__icon {
    padding: 0 10px 0 13px;
    color: var(--dk-text-faint, #b0bac4);
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: color 0.2s;
  }

  &:focus-within &__icon {
    color: $dk-orange;
  }

  &__input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    font-size: 0.84rem;
    color: var(--dk-text-primary, $dk-navy);
    padding-left: 4px !important;
    height: 38px;

    &::placeholder {
      color: var(--dk-text-muted, #b8c2cc);
    }
  }
}

// Select wrap
.dk-toolbar-select-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--dk-bg-input, #f7f9fb);
  border: 1px solid var(--dk-border-input, #e4eaee);
  border-radius: 9px;
  padding: 0 10px;
  min-width: 160px;
  transition: border-color 0.2s;

  &__icon {
    color: var(--dk-text-faint, #b0bac4);
    flex-shrink: 0;
  }

  &:focus-within {
    border-color: $dk-orange;
  }

  ::v-deep .el-input__inner {
    border: none !important;
    background: transparent !important;
    height: 38px;
    font-size: 0.84rem;
    color: var(--dk-text-primary, $dk-navy);
    padding-left: 0 !important;
    box-shadow: none !important;
  }

  ::v-deep .el-select .el-input.is-focus .el-input__inner {
    border: none !important;
  }
}

// Per page
.dk-per-page {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--dk-bg-input, #f7f9fb);
  border: 1px solid var(--dk-border-input, #e4eaee);
  border-radius: 9px;
  padding: 0 10px;
  height: 38px;

  &__icon {
    color: var(--dk-text-faint, #b0bac4);
    flex-shrink: 0;
  }

  &__select {
    border: none !important;
    background: transparent !important;
    font-size: 0.84rem;
    color: var(--dk-text-primary, $dk-navy);
    padding: 0 !important;
    height: auto;
    box-shadow: none !important;
    cursor: pointer;
  }
}

// ── Table wrapper ────────────────────────────────────
.dk-leads-table-wrap {
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: var(--dk-shadow-sm, 0 1px 4px rgba($dk-navy, 0.05));
}

.dk-leads-table {
  margin: 0;
  font-size: 0.84rem;

  ::v-deep thead th {
    background: var(--dk-bg-segment, $dk-navy) !important;
    color: var(--dk-text-secondary, rgba(255,255,255,0.85)) !important;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border: none !important;
    padding: 13px 16px;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    border-bottom: 1px solid var(--dk-border-light, #f0f3f6);
    transition: background 0.15s;

    &:hover {
      background: rgba($dk-orange, 0.03) !important;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  ::v-deep tbody td {
    padding: 12px 16px;
    vertical-align: middle;
    border: none !important;
    color: var(--dk-text-secondary, #374151);
  }
}

// Table cell atoms
.dk-leads-dados {
  font-weight: 500;
  color: var(--dk-text-primary, $dk-navy);
  font-size: 0.83rem;
}
.dk-leads-fluxo {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.83rem;
  color: var(--dk-text-secondary, #4b5563);
  &__icon {
    color: $dk-orange;
    flex-shrink: 0;
  }
}
.dk-leads-date {
  display: flex;
  align-items: center;
  color: var(--dk-text-muted, #6b7280);
  font-size: 0.8rem;
  white-space: nowrap;
}
.dk-leads-origem {
  display: flex;
  align-items: center;
  font-size: 0.79rem;
  color: var(--dk-text-secondary, #6b7280);
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dk-leads-origem--empty {
  color: var(--dk-text-faint, #d1d5db);
}

// Badge
.dk-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.71rem;
  font-weight: 700;
  letter-spacing: 0.02em;

  &--success {
    background: rgba(34,197,94,0.1);
    color: #15803d;
    border: 1px solid rgba(34,197,94,0.2);
  }
}

// Action buttons
.dk-leads-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dk-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;

  &--view {
    background: rgba($dk-navy, 0.08);
    color: $dk-navy;
    &:hover {
      background: $dk-navy;
      color: #fff;
    }
  }

  &--delete {
    background: rgba($dk-red, 0.08);
    color: $dk-red;
    &:hover {
      background: $dk-red;
      color: #fff;
    }
  }
}

// ── Empty state ─────────────────────────────────────
.dk-leads-empty {
  text-align: center;
  padding: 56px 24px;
}
.dk-leads-empty__icon {
  color: var(--dk-text-faint, #d1d9e0);
  display: block;
  margin: 0 auto 14px;
}
.dk-leads-empty__text {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--dk-text-muted, #8e9baa);
  margin: 0 0 4px;
}
.dk-leads-empty__sub {
  font-size: 0.78rem;
  color: var(--dk-text-faint, #b0bac4);
  margin: 0;
}

// ── Pagination ───────────────────────────────────────
.dk-leads-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: var(--dk-bg-card, #fff);
  border: 1px solid var(--dk-border, #edf1f4);
  border-radius: 10px;
  box-shadow: var(--dk-shadow-sm, 0 1px 4px rgba($dk-navy, 0.03));
}
.dk-leads-pagination__info {
  font-size: 0.78rem;
  color: var(--dk-text-muted, #9ca5b0);
}
.dk-pagination {
  ::v-deep .page-item.active .page-link {
    background: $dk-orange !important;
    border-color: $dk-orange !important;
    color: #fff !important;
  }
  ::v-deep .page-link {
    color: var(--dk-text-primary, $dk-navy);
    font-size: 0.8rem;
    border-color: var(--dk-border-input, #e4eaee);
    border-radius: 6px !important;
    margin: 0 2px;
    &:hover {
      background: rgba($dk-orange, 0.08);
      border-color: $dk-orange;
      color: $dk-orange;
    }
  }
}

// ── Modal delete ─────────────────────────────────────
::v-deep #modal-delete-lead {
  .modal-content {
    border-radius: 16px;
    overflow: hidden;
    border: none;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  }
  .modal-body {
    padding: 0 !important;
  }
}

.dk-delete-modal {
  border-radius: 16px;
  overflow: hidden;
}

.dk-delete-modal__header {
  background: $dk-red;
  padding: 24px 24px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.dk-delete-modal__icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(#fff, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dk-delete-modal__close {
  background: rgba(#fff, 0.12);
  border: none;
  border-radius: 8px;
  color: rgba(#fff, 0.8);
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(#fff, 0.25);
    color: #fff;
  }
}

.dk-delete-modal__body {
  padding: 20px 24px 8px;
}

.dk-delete-modal__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--dk-text-primary, $dk-navy);
  margin: 0 0 8px;
}

.dk-delete-modal__text {
  font-size: 0.85rem;
  color: var(--dk-text-secondary, #5a6272);
  line-height: 1.6;
  margin: 0;

  strong {
    color: $dk-red;
    font-weight: 600;
  }
}

.dk-delete-modal__footer {
  display: flex;
  gap: 10px;
  padding: 16px 24px 24px;
}

.dk-delete-modal__btn {
  flex: 1;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s, transform 0.1s;

  &:active { transform: scale(0.97); }

  &--cancel {
    background: var(--dk-bg-inset, #f3f6f8);
    color: var(--dk-text-secondary, #6b7280);
    border: 1px solid var(--dk-border-input, #e4eaee);

    &:hover { background: #eaecef; }
  }

  &--confirm {
    background: $dk-red;
    color: #fff;

    &:hover { opacity: 0.88; }
  }
}

// ── Lead name button (table cell) ─────────────────────
.dk-lead-name-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:hover .dk-lead-name-hint {
    opacity: 1;
    color: $dk-orange;
  }
  &:hover .dk-lead-avatar {
    background: $dk-orange;
  }
}

.dk-lead-avatar-ring {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 3px;
  transition: transform 0.2s;
}

// Avatar iniciais
.dk-lead-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: $dk-navy;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
  letter-spacing: 0.03em;
}

.dk-lead-name-btn:hover .dk-lead-avatar-ring {
  transform: scale(1.05);
}

.dk-lead-name-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.dk-lead-name-text {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--dk-text-primary, $dk-navy);
  line-height: 1.2;
}
.dk-lead-progress-hint {
  font-size: 0.68rem;
  color: #10b981;
  font-weight: 600;
}
.dk-lead-name-hint {
  font-size: 0.7rem;
  color: var(--dk-text-faint, #b0bac4);
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
}

// ── Lead detail modal ─────────────────────────────────
.dk-lead-detail {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 4px 0 8px;
}

.dk-lead-detail__hero {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--dk-border-light, #f0f3f6);

  .dk-lead-avatar {
    width: 48px;
    height: 48px;
    font-size: 0.88rem;
    flex-shrink: 0;
  }
}

.dk-lead-detail__name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--dk-text-primary, $dk-navy);
  line-height: 1.2;
}
.dk-lead-detail__flow {
  display: flex;
  align-items: center;
  font-size: 0.76rem;
  color: var(--dk-text-muted, #9ca5b0);
  margin-top: 2px;
}

.dk-lead-detail__section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dk-lead-detail__section-title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--dk-text-faint, #b0bac4);
  margin: 0;
}
.dk-lead-detail__grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dk-lead-detail__field {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  background: var(--dk-bg-inset, #f7f9fb);
  border-radius: 9px;
  border: 1px solid var(--dk-border, #edf1f4);
}
.dk-lead-detail__field-icon {
  color: $dk-orange;
  flex-shrink: 0;
  margin-top: 2px;
}
.dk-lead-detail__label {
  display: block;
  font-size: 0.7rem;
  color: var(--dk-text-muted, #9ca5b0);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
}
.dk-lead-detail__value {
  display: block;
  font-size: 0.84rem;
  color: var(--dk-text-primary, $dk-navy);
  font-weight: 500;
  word-break: break-word;
}

.dk-lead-detail__message {
  background: var(--dk-bg-inset, #f7f9fb);
  border: 1px solid var(--dk-border, #edf1f4);
  border-left: 3px solid $dk-orange;
  border-radius: 9px;
  padding: 12px 14px;
  font-size: 0.84rem;
  color: var(--dk-text-secondary, #374151);
  line-height: 1.6;
  white-space: pre-wrap;
}

.dk-lead-detail__footer {
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid var(--dk-border-light, #f0f3f6);
}
.dk-action-btn--lg {
  width: auto !important;
  height: auto !important;
  padding: 8px 16px !important;
  border-radius: 9px !important;
  font-size: 0.82rem !important;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

// ── Source / Canal badge ─────────────────────────────
.dk-source-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.71rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  white-space: nowrap;

  &--google  { background: rgba(#4285F4, .10); color: #4285F4; }
  &--meta    { background: rgba(#1877F2, .10); color: #1877F2; }
  &--utm     { background: rgba($dk-gold, .15); color: darken($dk-gold, 22%); }
  &--organic { background: rgba(#28c76f, .10); color: #28c76f; }
}
.dk-source-badge__svg {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  svg { display: block; }
}

// ── Trial expired: blur rows after 5 ──────────────────
::v-deep .dk-trial-blurred-row {
  filter: blur(5px);
  pointer-events: none;
  user-select: none;
  opacity: 0.6;
  transition: filter 0.3s;
}

.dk-leads-table--trial-expired {
  position: relative;
}

// ── Trial overlay card ─────────────────────────────────
.dk-trial-leads-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 30%, #fff 100%);
  z-index: 10;
  pointer-events: all;
}

.dk-trial-leads-overlay__card {
  text-align: center;
  padding: 28px 36px;
  background: #fff;
  border-radius: 16px;
  border: 2px solid rgba($dk-orange, 0.2);
  box-shadow: 0 8px 32px rgba($dk-navy, 0.12);
  max-width: 380px;
}

.dk-trial-leads-overlay__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba($dk-orange, 0.12), rgba($dk-gold, 0.08));
  color: $dk-orange;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}

.dk-trial-leads-overlay__title {
  font-size: 1.05rem;
  font-weight: 800;
  color: $dk-navy;
  margin: 0 0 8px;
}

.dk-trial-leads-overlay__text {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0 0 18px;
  line-height: 1.5;
}

.dk-trial-leads-overlay__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 28px;
  background: linear-gradient(135deg, $dk-orange, #f08535);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.15s;
  box-shadow: 0 4px 14px rgba($dk-orange, 0.35);

  &:hover {
    background: linear-gradient(135deg, darken($dk-orange, 8%), $dk-orange);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba($dk-orange, 0.45);
    color: #fff;
  }
}

// ── Lead row checkbox ────────────────────────────────────────
.dk-row-check {
  width: 16px;
  height: 16px;
  accent-color: $dk-orange;
  cursor: pointer;
  margin: 0;
}
// ── Ícone de enviado (substitui checkbox na lista) ────────────
.dk-row-sent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
}
// ── Badge "iAqFaz" na célula de nome ─────────────────────────
.dk-iaqfaz-sent-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  font-weight: 700;
  color: #7c3aed;
  background: rgba(124,58,237,0.08);
  border: 1px solid rgba(124,58,237,0.2);
  border-radius: 10px;
  padding: 1px 7px;
  line-height: 1.6;
  white-space: nowrap;
}

// ── Selection action bar ──────────────────────────────────────
.dk-leads-selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: linear-gradient(90deg, rgba($dk-orange, 0.08), rgba($dk-gold, 0.05));
  border: 1.5px solid rgba($dk-orange, 0.25);
  border-radius: 10px;
  gap: 12px;
}
.dk-leads-selection-bar__info {
  display: flex;
  align-items: center;
  font-size: 0.83rem;
  font-weight: 700;
  color: $dk-orange;
}
.dk-leads-selection-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dk-selection-btn {
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  border-radius: 8px;
  border: none;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &--iaqfaz {
    background: #7c3aed;
    color: #fff;
    &:hover:not(:disabled) { background: darken(#7c3aed, 8%); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  &--clear {
    background: var(--dk-bg-inset, #f3f6f8);
    color: var(--dk-text-secondary, #6b7280);
    border: 1px solid var(--dk-border-input, #e4eaee);
    padding: 7px 10px;
    &:hover { background: #eaecef; }
  }
}

// slide animation for action bar
.dk-bar-slide-enter-active, .dk-bar-slide-leave-active { transition: all 0.2s ease; }
.dk-bar-slide-enter, .dk-bar-slide-leave-to { opacity: 0; transform: translateY(-6px); }

// ── Kanban select mode button ─────────────────────────────────
.dk-cols-btn--selecting {
  border-color: #7c3aed !important;
  color: #7c3aed !important;
  background: rgba(#7c3aed, 0.06) !important;
}

// ── iAqFaz confirm + URL modal ────────────────────────────────
.dk-iaqfaz-modal__header--send { background: #7c3aed; }
.dk-iaqfaz-modal__header-text { flex: 1; }
.dk-iaqfaz-field-label {
  display: flex; align-items: center;
  font-size: 0.75rem; font-weight: 700; color: var(--dk-text-muted, #9ca5b0);
  text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px;
}
.dk-iaqfaz-url-input {
  width: 100%; padding: 9px 12px; border-radius: 8px;
  border: 1.5px solid var(--dk-border-input, #e4eaee);
  font-size: 0.82rem; color: var(--dk-text-primary, #0D3349);
  font-family: monospace; background: var(--dk-bg-input, #f7f9fb);
  outline: none; transition: border-color 0.2s;
  &:focus { border-color: #7c3aed; background: #fff; }
}
.dk-iaqfaz-url-hint {
  font-size: 0.74rem; color: var(--dk-text-faint, #b0bac4); margin: 6px 0 0; line-height: 1.5;
}
.dk-iaqfaz-confirm-btn {
  background: #7c3aed !important; color: #fff !important;
  &:hover:not(:disabled) { background: darken(#7c3aed, 8%) !important; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
}

// ── iAqFaz results modal ──────────────────────────────────────
::v-deep #modal-iaqfaz-results {
  .modal-content { border-radius: 16px; overflow: hidden; border: none; box-shadow: 0 20px 60px rgba(0,0,0,0.18); }
  .modal-body { padding: 0 !important; }
}
.dk-iaqfaz-modal { border-radius: 16px; overflow: hidden; }
.dk-iaqfaz-modal__header {
  padding: 22px 24px 18px;
  display: flex; align-items: flex-start; justify-content: space-between;
  &--success { background: #10B981; color: #fff; }
  &--partial  { background: #F59E0B; color: #fff; }
}
.dk-iaqfaz-modal__icon-wrap {
  width: 44px; height: 44px; border-radius: 50%;
  background: rgba(#fff, 0.15); color: #fff;
  display: flex; align-items: center; justify-content: center;
}
.dk-iaqfaz-modal__body { padding: 18px 24px 8px; }
.dk-iaqfaz-modal__title {
  font-size: 1rem; font-weight: 700;
  color: var(--dk-text-primary, $dk-navy); margin: 0 0 14px;
}
.dk-iaqfaz-modal__stats { display: flex; gap: 12px; margin-bottom: 14px; }
.dk-iaqfaz-stat {
  flex: 1; padding: 12px; border-radius: 10px; text-align: center;
  &--sent   { background: rgba(#10B981, 0.08); border: 1px solid rgba(#10B981, 0.2); }
  &--failed { background: rgba($dk-red, 0.08);  border: 1px solid rgba($dk-red,  0.2); }
}
.dk-iaqfaz-stat__num {
  display: block; font-size: 1.4rem; font-weight: 800;
  .dk-iaqfaz-stat--sent   & { color: #10B981; }
  .dk-iaqfaz-stat--failed & { color: $dk-red; }
}
.dk-iaqfaz-stat__label {
  display: block; font-size: 0.72rem; font-weight: 600;
  color: var(--dk-text-muted, #9ca5b0); text-transform: uppercase; letter-spacing: 0.05em;
}
.dk-iaqfaz-errors { display: flex; flex-direction: column; gap: 6px; margin-bottom: 8px; }
.dk-iaqfaz-error-item {
  display: flex; align-items: flex-start; gap: 6px; flex-wrap: wrap;
  font-size: 0.78rem; color: $dk-red;
  background: rgba($dk-red, 0.05); border-radius: 7px; padding: 7px 10px;
}
.dk-iaqfaz-error-msg {
  color: var(--dk-text-muted, #9ca5b0); font-size: 0.74rem;
  flex-basis: 100%; margin-top: 2px; word-break: break-all;
}
.dk-iaqfaz-modal__footer {
  padding: 12px 24px 20px;
  display: flex; justify-content: flex-end;
}

// ── Mobile Responsiveness ─────────────────────────────
@media (max-width: 768px) {
  .dk-leads-toolbar {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }
  .dk-leads-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .dk-leads-table {
    min-width: 600px;
  }
  .dk-action-btn {
    width: 38px;
    height: 38px;
  }
  .dk-leads-pagination {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  .dk-toolbar-select-wrap {
    min-width: 0 !important;
    width: 100%;
  }
}
@media (max-width: 480px) {
  .dk-leads-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  .dk-col-config {
    width: calc(100vw - 40px);
    right: 10px;
  }
  .dk-trial-leads-overlay__card {
    max-width: calc(100vw - 32px);
    padding: 20px;
  }
}
</style>
